import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, User, Tag, ArrowLeft, Share2, ChevronDown } from 'lucide-react';
import CTABanner from '../components/CTABanner';
import Footer from '../components/Footer';
import { useStructuredData } from '../hooks/useStructuredData';

const BlogPost = () => {
  const { slug, categorySlug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [openFaqIndex, setOpenFaqIndex] = useState(0); // First FAQ open by default
  const [bannerAd, setBannerAd] = useState(null);
  const [categories, setCategories] = useState([]);

  // Helper to construct full image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    
    // Handle if url is an object (thumbnail object)
    if (typeof url === 'object' && url.url) {
      url = url.url;
    }
    
    // SIMPLE: Just return the URL as-is
    // Upload endpoint now returns full URLs
    return url;
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (categories.length > 0 || !categorySlug) {
      fetchPost();
      fetchRelatedPosts();
      fetchBannerAd();
    }
  }, [slug, categorySlug, categories]);

  // Structured data comes from the backend so it matches what crawlers are served
  useStructuredData(post ? `/blog/${post.slug}` : null);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/blog/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
    }
  };

  const fetchPost = async () => {
    try {
      let response;
      
      // If we have a category slug, use the new URL structure
      if (categorySlug) {
        response = await api.get(`/blog/${categorySlug}/${slug}`);
      } else {
        // Legacy URL - try to fetch and check for redirect
        response = await api.get(`/blog/posts/${slug}`);
        
        // If the response includes a redirect_to field, perform a client-side redirect
        if (response.data.redirect_to) {
          navigate(response.data.redirect_to, { replace: true });
          return;
        }
      }
      
      setPost(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch post:', error);
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async () => {
    try {
      const response = await api.get('/blog/posts', {
        params: { limit: 3 }
      });
      setRelatedPosts(response.data.posts || []);
    } catch (error) {
      console.error('Failed to fetch related posts:', error);
    }
  };

  const fetchBannerAd = async () => {
    try {
      const response = await api.get('/blog/banner-ad');
      if (response.data.enabled && response.data.image_url) {
        setBannerAd(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch banner ad:', error);
    }
  };

  const shareOnSocial = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(post.title);
    const description = encodeURIComponent(post.excerpt || post.meta_description || '');
    
    const urls = {
      twitter: `https://x.com/intent/tweet?url=${url}&text=${title}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}&title=${title}&summary=${description}`
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EB2F5B] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto text-center py-20">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/blog')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  const primaryCategorySlug = categories.find((c) => c.id === post.primary_category)?.slug;
  const canonicalUrl = primaryCategorySlug
    ? `https://slicksignature.com/blog/${primaryCategorySlug}/${post.slug}`
    : `https://slicksignature.com/blog/${post.slug}`;

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{post.seo_title || post.title} | SlickSignature Blog</title>
        <meta name="description" content={post.meta_description || post.excerpt || post.title} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="SlickSignature" />
        <meta property="og:title" content={post.seo_title || post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt || post.title} />
        <meta property="og:url" content={canonicalUrl} />
        {post.thumbnail?.url && <meta property="og:image" content={getImageUrl(post.thumbnail.url).startsWith('http') ? getImageUrl(post.thumbnail.url) : `https://slicksignature.com${getImageUrl(post.thumbnail.url)}`} />}
        {post.thumbnail?.url && <meta property="og:image:alt" content={post.thumbnail.alt_text || post.title} />}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="article:published_time" content={post.published_at} />
        <meta property="article:modified_time" content={post.updated_at || post.published_at} />
        <meta property="article:author" content={post.author_name} />
        {primaryCategorySlug && <meta property="article:section" content={primaryCategorySlug.replace(/-/g, ' ')} />}
        {(post.tags || []).map((tag) => (
          <meta property="article:tag" content={tag} key={tag} />
        ))}
        <meta property="og:image:height" content="630" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.seo_title || post.title} />
        <meta name="twitter:description" content={post.meta_description || post.excerpt || post.title} />
        {post.thumbnail?.url && <meta name="twitter:image" content={getImageUrl(post.thumbnail.url).startsWith('http') ? getImageUrl(post.thumbnail.url) : `https://slicksignature.com${getImageUrl(post.thumbnail.url)}`} />}
      </Helmet>

      {/* Header */}
      <header className="bg-black text-white py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <Link to="/">
              <img 
                src="https://cdn.slicksignature.com/landing/slicksignature-logo-white.png"
                alt="SlickSignature"
                className="h-12"
              />
            </Link>
            <div className="flex gap-4">
              <Button 
                onClick={() => navigate('/checkout?plan=business')} 
                className="bg-white text-[#EB2F5B] hover:bg-gray-100"
              >
                Start Free Trial
              </Button>
              <Button onClick={() => navigate('/blog')} variant="outline" className="text-white border-white">
                Blog
              </Button>
              <Button onClick={() => navigate('/login')} variant="outline" className="text-white border-white">
                Login
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <article className="lg:col-span-2">
            {/* Featured Image */}
            {post.thumbnail?.url && (
              <img
                src={getImageUrl(post.thumbnail.url)}
                alt={post.thumbnail.alt_text || post.title}
                className="w-full h-96 object-cover rounded-lg mb-8"
              />
            )}

            {/* Breadcrumb - Moved below featured image */}
            <nav className="text-sm text-gray-600 mb-6">
              <Link to="/" className="hover:text-[#EB2F5B]">Home</Link>
              {' > '}
              <Link to="/blog" className="hover:text-[#EB2F5B]">Blog</Link>
              {post.primary_category && categories.length > 0 && (
                <>
                  {' > '}
                  <Link 
                    to={`/blog/category/${categories.find(c => c.id === post.primary_category)?.slug || 'uncategorized'}`} 
                    className="hover:text-[#EB2F5B]"
                  >
                    {categories.find(c => c.id === post.primary_category)?.name || 'Category'}
                  </Link>
                </>
              )}
              {' > '}
              <span className="text-gray-900">{post.title}</span>
            </nav>

            {/* Meta */}
            <div className="flex items-center gap-6 text-sm text-gray-600 mb-6">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{post.author_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{new Date(post.published_at).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-4xl font-bold text-gray-900 mb-6">{post.title}</h1>

            {/* Excerpt */}
            {post.excerpt && (
              <p className="text-xl text-gray-600 mb-8 italic">{post.excerpt}</p>
            )}

            {/* Social Share */}
            <div className="flex items-center gap-3 mb-8 pb-8 border-b">
              <span className="text-sm text-gray-600">Share:</span>
              <Button onClick={() => shareOnSocial('twitter')} size="sm" variant="outline">
                <Share2 className="h-4 w-4 mr-2" />
                X
              </Button>
              <Button onClick={() => shareOnSocial('facebook')} size="sm" variant="outline">
                <Share2 className="h-4 w-4 mr-2" />
                Facebook
              </Button>
              <Button onClick={() => shareOnSocial('linkedin')} size="sm" variant="outline">
                <Share2 className="h-4 w-4 mr-2" />
                LinkedIn
              </Button>
            </div>

            {/* Content - Enhanced prose styling for proper formatting */}
            <div 
              className="prose prose-lg max-w-none 
                prose-headings:font-bold prose-headings:text-gray-900
                prose-h2:text-3xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-2xl prose-h3:mt-6 prose-h3:mb-3
                prose-h4:text-xl prose-h4:mt-4 prose-h4:mb-2
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                prose-a:text-[#EB2F5B] prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-ul:list-disc prose-ul:ml-6 prose-ul:mb-4
                prose-ol:list-decimal prose-ol:ml-6 prose-ol:mb-4
                prose-li:text-gray-700 prose-li:mb-2
                prose-blockquote:border-l-4 prose-blockquote:border-[#EB2F5B] prose-blockquote:pl-4 prose-blockquote:italic
                prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
                prose-img:rounded-lg prose-img:shadow-md"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag className="h-4 w-4 text-gray-600" />
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* FAQs Section */}
            {post.faqs && post.faqs.length > 0 && (
              <div className="mt-12 pt-8 border-t">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
                <div className="space-y-3">
                  {post.faqs.map((faq, index) => (
                    <div 
                      key={index} 
                      className="border border-gray-200 rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow"
                    >
                      {/* Accordion Header */}
                      <button
                        onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                        className="w-full px-6 py-4 text-left flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <h3 className="text-lg font-semibold text-gray-900 pr-8">
                          {faq.question}
                        </h3>
                        <ChevronDown 
                          className={`h-5 w-5 text-gray-600 flex-shrink-0 transition-transform ${
                            openFaqIndex === index ? 'transform rotate-180' : ''
                          }`}
                        />
                      </button>
                      
                      {/* Accordion Content */}
                      {openFaqIndex === index && (
                        <div className="px-6 py-4 bg-white border-t border-gray-200">
                          <p className="text-gray-700 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </article>

          {/* Sticky Sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-6 lg:self-start">
            {/* CTA Card */}
            <Card className="bg-gradient-to-br from-[#EB2F5B] to-[#d02850] text-white">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-2">Professional Email Signatures</h3>
                <p className="text-white/90 mb-4">
                  Create branded signatures for your entire team in minutes.
                </p>
                <div className="space-y-2 mb-4 text-sm">
                  <p>✓ 15-Day Free Trial</p>
                  <p>✓ Never struggle with signatures again</p>
                  <p>✓ Setup in 5 Minutes</p>
                </div>
                <Button 
                  onClick={() => navigate('/checkout?plan=business')}
                  className="w-full bg-white text-[#EB2F5B] hover:bg-gray-100"
                >
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold mb-4">Related Posts</h3>
                  <div className="space-y-4">
                    {relatedPosts.map((related) => (
                      <Link
                        key={related.id}
                        to={`/blog/${related.slug}`}
                        className="block hover:text-[#EB2F5B]"
                      >
                        <h4 className="font-semibold text-sm">{related.title}</h4>
                        <p className="text-xs text-gray-600 mt-1">
                          {new Date(related.published_at).toLocaleDateString()}
                        </p>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Banner Ad */}
            {bannerAd && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <a 
                  href={bannerAd.link_url || '#'} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <img
                    src={getImageUrl(bannerAd.image_url)}
                    alt={bannerAd.alt_text || 'Advertisement'}
                    className="w-full h-auto object-contain"
                    style={{ maxWidth: '100%', height: 'auto' }}
                  />
                </a>
              </div>
            )}
          </aside>
        </div>
      </div>

      <CTABanner />
      <Footer />
    </div>
  );
};

export default BlogPost;
